import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileDown, FileX2, List, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { ChartCard } from '@/components/dashboard/ChartCard';
import { ContractorDocuments } from '@/components/contractors/ContractorDocuments';
import { StatementExportDialog } from '@/components/sales/StatementExportDialog';
import {
  MONTH_LABELS, monthKey, formatPLN, formatAxis, plnTooltipFormatter, pctTooltipFormatter,
} from '@/components/dashboard/chart-utils';
import { t } from '@/i18n';
import { useAuth } from '@/hooks/useAuth';
import { useTabParam } from '@/hooks/useTabParam';
import { canExportSalesStatement, canSeeTurnover } from '@/lib/permissions';
import { useContractorsApi } from '@/services/api';
import type { ContractorDashboardResponse } from '@ip/shared-types';

/** `?tab=documents` deep-links the documents pane (the reminder bell does). */
const CONTRACTOR_TABS = ['dashboard', 'documents'] as const;
type ContractorTab = (typeof CONTRACTOR_TABS)[number];

interface ContractorDetailProps {
  companyNip: string;
  companyName: string;
  contractorId: string;
}

function StatTile({ title, value, tone }: { title: string; value: string; tone?: 'positive' | 'negative' }) {
  const toneClass =
    tone === 'positive'
      ? 'text-emerald-600 dark:text-emerald-400'
      : tone === 'negative'
        ? 'text-red-600 dark:text-red-400'
        : '';
  return (
    <Card className="py-4">
      <CardContent className="px-4">
        <p className="text-muted-foreground truncate text-xs font-medium" title={title}>{title}</p>
        <p className={`mt-1 truncate font-mono text-lg font-semibold tabular-nums ${toneClass}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

export function ContractorDetail({ companyNip, companyName, contractorId }: ContractorDetailProps) {
  const [data, setData] = useState<ContractorDashboardResponse | null>(null);
  const [nameOnly, setNameOnly] = useState<string | null>(null);
  const [statementOpen, setStatementOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [tab, setTab] = useTabParam(CONTRACTOR_TABS, 'dashboard');
  const navigate = useNavigate();
  const { permissions, isSuperAdmin } = useAuth();
  // Turnover is invoice data (decision 2026-09-10): a contractors-only caller
  // (the 'crm' role) is 403'd on the dashboard endpoint, so it is never
  // requested — only the contractor record, for the page title. Documents,
  // data, bank accounts and tags stay fully usable.
  const showTurnover = canSeeTurnover(permissions, companyNip);
  const { getContractorDashboard, getContractor } = useContractorsApi();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (!showTurnover) {
        const contractor = await getContractor(companyNip, contractorId);
        setNameOnly(contractor.name);
        setData(null);
        return;
      }
      const result = await getContractorDashboard(companyNip, contractorId, year);
      setData(result);
      if (result.availableYears.length > 0 && !result.availableYears.includes(year)) {
        setYear(result.availableYears[0]);
      }
    } catch (error) {
      toast.error(t('errors.loadingFailed'));
      console.error('Contractor dashboard load failed:', error);
    } finally {
      setLoading(false);
    }
  }, [companyNip, contractorId, year, showTurnover, getContractorDashboard, getContractor]);

  useEffect(() => { loadData(); }, [loadData]);

  const contractorName = data?.contractor?.name || nameOnly || contractorId;
  // Customer statement PDF: only for a customer, and only with 'sales' access
  // that is not tag-restricted (the document is a per-customer total). That
  // access also opens the turnover, so the role comes from the dashboard.
  const contractorRole = data?.contractor?.role;
  const showStatement =
    (isSuperAdmin || canExportSalesStatement(permissions, companyNip)) &&
    (contractorRole === 'customer' || contractorRole === 'both');
  const salesSummary = data?.salesSummary;
  const hasData = data && (data.summary.invoiceCount > 0 || (salesSummary?.invoiceCount ?? 0) > 0);

  // Dual-direction monthly turnover: purchases (we buy from them) vs sales
  // (we sell to them), net PLN.
  const turnoverData = data
    ? MONTH_LABELS.map((label, i) => {
        const key = monthKey(data.year, i);
        const purchase = data.monthlyTurnover.find((d) => d.month === key);
        const sale = (data.monthlySales ?? []).find((d) => d.month === key);
        return { month: label, purchases: purchase?.totalNet ?? 0, sales: sale?.totalNet ?? 0 };
      })
    : [];
  const turnoverConfig: ChartConfig = {
    purchases: { label: t('contractors.detail.purchasesSeries'), color: 'var(--chart-1)' },
    sales: { label: t('contractors.detail.salesSeries'), color: 'var(--chart-2)' },
  };

  const shareData = data
    ? MONTH_LABELS.map((label, i) => {
        const key = monthKey(data.year, i);
        const purchase = data.monthlyShare.find((d) => d.month === key);
        const sale = (data.monthlySalesShare ?? []).find((d) => d.month === key);
        return {
          month: label,
          purchaseShare: purchase ? Math.round(purchase.sharePercent * 100) / 100 : 0,
          salesShare: sale ? Math.round(sale.sharePercent * 100) / 100 : 0,
        };
      })
    : [];
  const shareConfig: ChartConfig = {
    purchaseShare: { label: t('contractors.detail.purchaseShare'), color: 'var(--chart-5)' },
    salesShare: { label: t('contractors.detail.salesShare'), color: 'var(--chart-2)' },
  };
  const balanceNet = data?.balanceNet ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 mb-1"
            onClick={() => navigate(`/${companyNip}/contractors`)}
          >
            <ArrowLeft className="size-4" />
            {t('contractors.detail.back')}
          </Button>
          <h1 className="text-lg font-semibold tracking-tight">
            {t('contractors.detail.title').replace('{name}', contractorName)}
          </h1>
          {companyName && <p className="text-muted-foreground text-sm">{companyName}</p>}
        </div>
        <div className="flex items-center gap-2">
          {showStatement && (
            <Button variant="outline" onClick={() => setStatementOpen(true)}>
              <FileDown className="size-4" />
              {t('sales.statement.button')}
            </Button>
          )}
          {data && data.availableYears.length > 0 && (
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                {data.availableYears.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {showStatement && (
        <StatementExportDialog
          companyNip={companyNip}
          open={statementOpen}
          onOpenChange={setStatementOpen}
          contractor={{ id: contractorId, name: contractorName }}
        />
      )}

      <Tabs value={tab} onValueChange={(value) => setTab(value as ContractorTab)}>
        <TabsList>
          <TabsTrigger value="dashboard">{t('contractors.detail.tabDashboard')}</TabsTrigger>
          <TabsTrigger value="documents">{t('contractors.detail.tabDocuments')}</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="flex flex-col gap-4 pt-2">
      {!showTurnover && (
        <Card className="py-6">
          <CardContent className="text-muted-foreground flex flex-col items-center gap-3 px-6 text-center">
            <Lock className="size-8 opacity-50" />
            <p className="text-sm">{t('contractors.turnoverHidden')}</p>
          </CardContent>
        </Card>
      )}

      {showTurnover && loading && (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-20" />)}
          </div>
          <Skeleton className="h-[380px]" />
          <Skeleton className="h-[330px]" />
        </div>
      )}

      {showTurnover && !loading && !hasData && (
        <div className="text-muted-foreground flex flex-col items-center gap-3 py-20">
          <FileX2 className="size-10 opacity-50" />
          <p className="text-sm">{t('contractors.detail.noData')}</p>
        </div>
      )}

      {showTurnover && !loading && hasData && data && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            <StatTile
              title={t('contractors.detail.purchasesNet')}
              value={`${formatPLN(data.summary.totalTurnoverNet)} PLN`}
            />
            <StatTile
              title={t('contractors.detail.salesNet')}
              value={`${formatPLN(salesSummary?.totalTurnoverNet ?? 0)} PLN`}
            />
            <StatTile
              title={t('contractors.detail.balanceNet')}
              value={`${balanceNet > 0 ? '+' : ''}${formatPLN(balanceNet)} PLN`}
              tone={balanceNet > 0 ? 'positive' : balanceNet < 0 ? 'negative' : undefined}
            />
            <StatTile
              title={t('contractors.detail.invoiceCounts')}
              value={`${data.summary.invoiceCount} / ${salesSummary?.invoiceCount ?? 0}`}
            />
            <StatTile
              title={t('contractors.detail.lastPurchaseDate')}
              value={data.summary.lastInvoiceDate || '-'}
            />
            <StatTile
              title={t('contractors.detail.lastSaleDate')}
              value={salesSummary?.lastInvoiceDate || '-'}
            />
          </div>

          <ChartCard title={t('contractors.detail.monthlyTurnover')}>
            <ChartContainer config={turnoverConfig} className="h-[300px] w-full">
              <BarChart data={turnoverData}>
                <CartesianGrid vertical={false} strokeOpacity={0.4} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} />
                <YAxis tickFormatter={formatAxis} tickLine={false} axisLine={false} width={64} />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      labelFormatter={(label) => `${label} ${data.year}`}
                      formatter={plnTooltipFormatter(turnoverConfig)}
                    />
                  }
                />
                <ChartLegend content={<ChartLegendContent />} />
                <Bar dataKey="purchases" fill="var(--color-purchases)" radius={[4, 4, 0, 0]} maxBarSize={28} />
                <Bar dataKey="sales" fill="var(--color-sales)" radius={[4, 4, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ChartContainer>
          </ChartCard>

          <ChartCard title={t('contractors.detail.monthlyShare')}>
            <ChartContainer config={shareConfig} className="h-[250px] w-full">
              <BarChart data={shareData}>
                <CartesianGrid vertical={false} strokeOpacity={0.4} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} />
                <YAxis tickFormatter={(v: number) => `${v}%`} tickLine={false} axisLine={false} width={48} />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      labelFormatter={(label) => `${label} ${data.year}`}
                      formatter={pctTooltipFormatter(shareConfig)}
                    />
                  }
                />
                <ChartLegend content={<ChartLegendContent />} />
                <Bar dataKey="purchaseShare" fill="var(--color-purchaseShare)" radius={[4, 4, 0, 0]} maxBarSize={28} />
                <Bar dataKey="salesShare" fill="var(--color-salesShare)" radius={[4, 4, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ChartContainer>
          </ChartCard>

          <div>
            <Button
              variant="outline"
              onClick={() =>
                navigate(`/${companyNip}/invoices?search=${encodeURIComponent(contractorName)}`)
              }
            >
              <List className="size-4" />
              {t('contractors.detail.viewInvoices')}
            </Button>
          </div>
        </>
      )}
        </TabsContent>

        <TabsContent value="documents" className="pt-2">
          <ContractorDocuments companyNip={companyNip} contractorId={contractorId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
